"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef } from 'react';

// Hook to detect when an element is in viewport
export const useIntersectionObserver = t0 => {
  const $ = _c(11);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const elementRef = useRef(null);
  let t2;
  if ($[2] !== hasBeenVisible || $[3] !== options) {
    t2 = () => {
      const element = elementRef.current;
      if (!element) {
        return;
      }
      const observer = new IntersectionObserver(t3 => {
        const [entry] = t3;
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting && !hasBeenVisible) {
          setHasBeenVisible(true);
        }
      }, {
        threshold: 0.2,
        ...options
      });
      observer.observe(element);
      return () => {
        observer.disconnect();
      };
    };
    $[2] = hasBeenVisible;
    $[3] = options;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== hasBeenVisible || $[6] !== options.threshold) {
    t3 = [hasBeenVisible, options.threshold];
    $[5] = hasBeenVisible;
    $[6] = options.threshold;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== hasBeenVisible || $[9] !== isVisible) {
    t4 = {
      elementRef,
      isVisible,
      hasBeenVisible
    };
    $[8] = hasBeenVisible;
    $[9] = isVisible;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};

// Animated rating hook that returns both display value and progress
export const useAnimatedRating = (targetValue, t0) => {
  const $ = _c(11);
  const shouldAnimate = t0 === undefined ? true : t0;
  const [displayValue, setDisplayValue] = useState(0);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  let t1;
  if ($[0] !== animatedProgress || $[1] !== displayValue || $[2] !== shouldAnimate || $[3] !== targetValue) {
    t1 = () => {
      if (!shouldAnimate) {
        setDisplayValue(0);
        setAnimatedProgress(0);
        return;
      }
      if (displayValue === targetValue && animatedProgress === targetValue) {
        return;
      }
      const startValue = displayValue;
      const difference = targetValue - startValue;
      const startTime = Date.now();
      let animationFrame;
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / 1200, 1);
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentValue = startValue + difference * easeOutCubic;
        const roundedValue = Math.round(currentValue);
        setDisplayValue(roundedValue);
        setAnimatedProgress(currentValue);
        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          setDisplayValue(targetValue);
          setAnimatedProgress(targetValue);
        }
      };
      animationFrame = requestAnimationFrame(animate);
      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    };
    $[0] = animatedProgress;
    $[1] = displayValue;
    $[2] = shouldAnimate;
    $[3] = targetValue;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== shouldAnimate || $[6] !== targetValue) {
    t2 = [targetValue, shouldAnimate];
    $[5] = shouldAnimate;
    $[6] = targetValue;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  if ($[8] !== animatedProgress || $[9] !== displayValue) {
    t3 = {
      displayValue,
      animatedProgress
    };
    $[8] = animatedProgress;
    $[9] = displayValue;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};